/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CommentIcon from '@mui/icons-material/Comment';
import SearchIcon from '@mui/icons-material/Pageview';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import SvgIcon, { SvgIconProps } from '@mui/material/SvgIcon';
import Tooltip from '@mui/material/Tooltip';

import styles from './documentVersionFlags.module.scss';
import { useAppSelector } from '../../../app/hooks';
import { selectCreatorMode } from '../chat/chatSlice';
import { DocumentVersionFlag, flagsMetadata } from './documentVersionFlags';
import FlagIcon from './FlagIcon';

const flagsMetadataMap = new Map(flagsMetadata.map((x) => [x.name, x]));
/** Icon for MUSC and PROV only library flags.
 *  Make sure no other css formatting leaks in.
 */
const TextIcon = (props: SvgIconProps & { text: string }) => (
  <span className={styles.textIcon} style={{ background: props.htmlColor, fill: props.fill }}>
    {props.text}
  </span>
);

const LightningBoltIcon = (props: SvgIconProps) => (
  <SvgIcon {...props} viewBox="-5 0 24 21">
    <path
      d="M1 13L8 1V9H13L6 21V13H1Z"
      fill={props.fill || '#FFF400'}
      stroke={props.stroke || 'var(--indigonavyblue)'}
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </SvgIcon>
);

const TrackChangesIcon = (props: SvgIconProps) => (
  <SvgIcon {...props} viewBox="0 0 20 20">
    <rect x="2" y="1" width="14" height="17" fill="white" />
    <path d="M6.182 5.514a.643.643 0 0 0-.636.64v-.001a.636.636 0 0 0 .636.639h5.682a.64.64 0 0 0 .636-.638.637.637 0 0 0-.636-.64zm0 2.986a.642.642 0 0 0-.588.882.64.64 0 0 0 .588.395h5.682a.64.64 0 0 0 .636-.639.635.635 0 0 0-.636-.638zm-.002 3a.64.64 0 0 0 0 1.278h3.133a.64.64 0 0 0 0-1.278zm5.592 6.808.154-.23q-.292-.148-.328-.28-.037-.135.076-.551.093-.248.11-.393a3 3 0 0 0 0-.479q-.206-.614-.186-.83.02-.218.313-.57l4.465-6.207q.534-.9 2.11.118 1.576 1.021 1.177 1.772l-3.876 6.833q-.157.407-.347.515c-.128.07-.4.119-.813.139a3 3 0 0 0-.487.21q-.191.135-.347.31-.29.261-.416.262-.095 0-.346-.15l-.204.27zm-.184.274 1.039.727-.26.34h-1.496z"></path>
    <path
      d="M3.272 1A1.28 1.28 0 0 0 2 2.28v14.439a1.276 1.276 0 0 0 1.272 1.28h6.838a2.1 2.1 0 0 1 .003-.61 4 4 0 0 1 .156-.67l.025-.069v-.008l-.042-.142H3.5V2.46s11 .002 11 .005v6.341l.627-.872c.204-.32.49-.614.873-.819V2.281c0-.707-.569-1.28-1.272-1.28zM16 9.29l-1.5 2.085V16.5h-2.708q.007.177-.007.354l-.013.075-.001.005a3 3 0 0 1-.097.312q-.04.152-.062.266a.7.7 0 0 0-.014.287.25.25 0 0 0 .068.105 1 1 0 0 0 .088.074l.017.011.016.01h.175L14.73 18c.697 0 1.271-.573 1.271-1.281v-7.43Z"
      clipRule="evenodd"
    />
  </SvgIcon>
);

export function IconForFlag({ flag, off, ...props }: { flag: DocumentVersionFlag; off?: boolean } & SvgIconProps) {
  const isCreatorMode = useAppSelector(selectCreatorMode);

  if (!flag) return null;

  switch (flag) {
    case DocumentVersionFlag.contextualized_forced:
    case DocumentVersionFlag.contextualized: {
      return isCreatorMode ? (
        <LightningBoltIcon {...props} fill={off ? '#eeeeee' : undefined} stroke={off ? '#bbbbbb' : undefined} />
      ) : null;
    }
    case DocumentVersionFlag.star: {
      return off ? (
        <StarBorderIcon {...props} htmlColor="var(--gray84)" />
      ) : (
        <StarIcon {...props} htmlColor="#FFD300" stroke="#FDB515" />
      );
    }
    case DocumentVersionFlag.magnifying_glass: {
      return off ? <SearchIcon {...props} htmlColor="var(--gray84)" /> : <SearchIcon {...props} />;
    }
    case DocumentVersionFlag.deprecated: {
      return off ? <DeleteSweepIcon {...props} htmlColor="var(--gray84)" /> : <DeleteSweepIcon {...props} />;
    }
    case DocumentVersionFlag.internal_only: {
      return off ? <VisibilityOffIcon {...props} htmlColor="var(--gray84)" /> : <VisibilityOffIcon {...props} />;
    }
    case DocumentVersionFlag.new: {
      return off ? (
        <TextIcon text="New" fill="#FFA012" {...props} htmlColor="var(--gray84)" />
      ) : (
        <TextIcon text="New" fill="#FFA012" {...props} htmlColor="#FFA012" />
      );
    }
    case DocumentVersionFlag.has_comments_from_ai: {
      return null; // Prefer has_comments_needing_attention
    }
    case DocumentVersionFlag.has_comments_needing_attention: {
      return <CommentIcon {...props} htmlColor="#f6602d" style={{ marginTop: 4 }} />;
    }
    case DocumentVersionFlag.providence_only_library: {
      return <TextIcon {...props} text="PROV" />;
    }
    case DocumentVersionFlag.musc_only_library: {
      return <TextIcon {...props} text="MUSC" />;
    }
    case DocumentVersionFlag.demo_library: {
      return <TextIcon {...props} text="DEMO" />;
    }
    case DocumentVersionFlag.demorg_library: {
      return <TextIcon {...props} text="DEMORG" />;
    }
    case DocumentVersionFlag.red_flag: {
      // When off, make a tiny bit red to indicate that it's still a red flag
      return <FlagIcon stroke={off ? '#dd9999' : '#FF9999'} fill={off ? 'none' : '#CD0000'} />;
    }
    case DocumentVersionFlag.yellow_flag: {
      // When off, make a tiny bit yellow
      return <FlagIcon stroke={off ? '#c9c7a8' : '#f5e44c'} fill={off ? 'none' : '#f5e44c'} />;
    }
    case DocumentVersionFlag.tracked_changes: {
      return <TrackChangesIcon {...props} htmlColor={off ? 'var(--gray84)' : '#4a90e2'} />;
    }
    case DocumentVersionFlag.toc: {
      return <FormatListBulletedIcon {...props} htmlColor={off ? 'var(--gray84)' : '#267494'} />;
    }
    default:
      return null;
  }
}

export function IconsForDocumentFlags({
  flags,
  containerStyle,
  ...props
}: { flags: string[] | undefined; containerStyle?: React.CSSProperties } & SvgIconProps) {
  const isCreator = useAppSelector(selectCreatorMode);
  if (!flags || flags.length === 0) return null;
  if (!Array.isArray(flags)) {
    console.error('Flags is not an array:', flags);
    return null;
  }
  const hasContextualized = flags.includes(DocumentVersionFlag.contextualized);
  flags = flags.filter(
    (x) =>
      (!hasContextualized || x !== DocumentVersionFlag.contextualized_forced) &&
      (isCreator || flagsMetadataMap.get(x as DocumentVersionFlag)?.visibleInConsumer)
  );
  if (flags.length === 0) return null;
  return (
    <div className={styles.container} style={containerStyle}>
      {flags.map((flag) => (
        <Tooltip
          title={flagsMetadataMap.get(flag as DocumentVersionFlag)?.friendlyName || flag}
          key={flag}
          enterDelay={0}
          disableInteractive
        >
          <span style={{ height: 24 }}>
            <IconForFlag
              flag={flag as DocumentVersionFlag}
              sx={flag === 'new' ? { width: 36 } : undefined}
              {...props}
            />
          </span>
        </Tooltip>
      ))}
    </div>
  );
}

/** In some places we can't render flags, so have a crude unicode backup function */
export function unicodeForFlags(flags: string[] | undefined) {
  if (!flags || flags.length === 0) return '';
  return flags
    .map((flag) => unicodeForFlag(flag))
    .filter(Boolean)
    .join(' ');
}

/** A crude unicode backup for a single flag */
function unicodeForFlag(flag: string) {
  switch (flag) {
    case DocumentVersionFlag.star:
      return '⭐';
    case DocumentVersionFlag.magnifying_glass:
      return '🔍';
    case DocumentVersionFlag.deprecated:
      return '🗑️';
    case DocumentVersionFlag.internal_only:
      return '🔒';
    // There's no yellow flag emoji, so use red square and yellow square
    case DocumentVersionFlag.red_flag:
      return '🟥';
    case DocumentVersionFlag.yellow_flag:
      return '🟨';
    // lightning bolt for contextualized
    case DocumentVersionFlag.contextualized:
    case DocumentVersionFlag.contextualized_forced:
      return '⚡';
    case DocumentVersionFlag.toc:
      return '☰';
    default:
      return null;
  }
}
